import { useState, useEffect } from 'react';

import { useTheme } from '@mui/material';
import { UserChipField } from '@ragu-components/ord-components';
import { CalendarFields } from '@ragu-components/ord-types';
import { TextField } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';
import { Resources } from 'src/lib/resources';

import ResourceList from '../../components/ResourceList';
import NodeControlForm from '../NodeControlForm';

import type { NodeControlsProps } from '../../dataProvider/types';
import type { Calendar } from '@ragu-components/ord-types';
import type { FilterPayload, RaRecord } from 'react-admin';

export interface CalendarType extends Record<string, unknown> {
  calendar: Calendar | null;
}

const getCalendarProps = (
  isMobility: boolean,
  hasOwner: boolean,
  record: RaRecord | undefined
): { resource: string; filter: FilterPayload | undefined } => {
  const resource = isMobility && !hasOwner ? `me/${Resources.calendars}` : Resources.calendars;
  let filter;
  if (isMobility && hasOwner && record !== undefined) {
    filter = {
      active: 'fiql',
      fiql: `owner.id==${record.owner.id as string},owner.id=isnull=true`,
    };
  } else if (!isMobility) {
    filter = {
      active: 'fiql',
      fiql: `owner.id=isnull=true`,
    };
  }

  return { resource, filter };
};

interface CalendarControlsProps extends NodeControlsProps {
  updateNodeData: (data: Record<string, unknown>) => void;
  data: CalendarType;
}

export default function CalendarControls({
  updateNodeData,
  data,
  record,
  resource,
}: CalendarControlsProps): React.ReactElement {
  const isMobility = resource.includes('mobility_configurations');
  /**
   * If flowchart has an owner, it means it is a mobility received from the global mobilities endpoint
   */
  const hasOwner = record !== undefined && 'owner' in record && record.owner !== null && record.owner !== undefined;
  const theme = useTheme();

  const [calendar, setCalendar] = useState<Calendar | null>(data.calendar ?? null);

  const onCalendarDataUpdate = (event: React.FormEvent): void => {
    event.preventDefault();
    flowchartLogger.debug('CalendarControls: onCalendarDataUpdate', { calendar });
    if (calendar === undefined || calendar === null) {
      flowchartLogger.debug('CalendarControls: calendar is undefined or empty. Update cancelled.');
      return;
    }
    updateNodeData({ calendar });
  };

  useEffect(() => {
    flowchartLogger.debug('CalendarControls: calendar changed', { calendar });
    updateNodeData({ calendar });
  }, [calendar, updateNodeData]);

  return (
    <NodeControlForm
      onSubmit={onCalendarDataUpdate}
      isDisabled={calendar === undefined || calendar === null}
      data={data}
      updateNodeData={updateNodeData}
    >
      <ResourceList
        {...getCalendarProps(isMobility, hasOwner, record)}
        selectedResourceId={calendar !== null ? calendar.id : null}
        onRowClick={(_id, _resource, record) => {
          setCalendar(record as Calendar);
          return false;
        }}
      >
        <TextField source={CalendarFields.title} />
        <TextField source={CalendarFields.description} />
        {/* eslint-disable-next-line */}
        {isMobility && hasOwner && (
          <UserChipField
            sortable={false}
            source={CalendarFields.owner}
            chipProps={{ sx: { backgroundColor: theme.palette.grey['300'] } }}
          />
        )}
      </ResourceList>
    </NodeControlForm>
  );
}
